import { useRef } from "react";
import {
    motion,
    useMotionValue,
    useScroll,
    useSpring,
    useTransform,
} from "framer-motion";
import { HeroLine } from "./motion";

export default function Hero({ onScrollDown }) {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const px = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), {
        stiffness: 45,
        damping: 20,
    });
    const py = useSpring(useTransform(my, [-0.5, 0.5], [-10, 10]), {
        stiffness: 45,
        damping: 20,
    });
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yBg = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
    const frameScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

    const onMouseMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            id="hero"
            ref={ref}
            data-testid="hero-section"
            onMouseMove={onMouseMove}
            className="relative min-h-screen overflow-hidden"
        >
            <motion.div
                data-testid="hero-photo-slot"
                style={{ scale: frameScale }}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                className="group absolute inset-0 border-b border-[#D4AF37]/25 bg-[#0B0B0C]"
            >
                <motion.div style={{ x: px, y: py }} className="absolute -inset-6">
                    <img
                        src="/hero-daniel.png"
                        alt="Daniel Dulude, photographe — LE GARS DE LA PHOTO"
                        data-testid="hero-photo"
                        className="absolute inset-0 h-full w-full object-contain object-top sm:object-center"
                    />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_35%,rgba(212,175,55,0.07),transparent_62%)]" />
                    <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(5,5,5,0.35),transparent_45%,transparent_65%,rgba(5,5,5,0.4))]" />
                    <div className="noise-fill absolute inset-0" />
                </motion.div>
                <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#050505] to-transparent" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[50%] bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent sm:hidden" />
                <div className="pointer-events-none absolute inset-0 hidden bg-[linear-gradient(100deg,#050505_0%,rgba(5,5,5,0.72)_30%,transparent_58%)] sm:block" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-[24%] bg-gradient-to-t from-[#050505]/85 to-transparent sm:block" />
                <div className="pointer-events-none absolute inset-4 border border-[#D4AF37]/15 sm:inset-6" />
                <span className="pointer-events-none absolute left-4 top-4 h-9 w-9 border-l-2 border-t-2 border-[#D4AF37] sm:left-6 sm:top-6" />
                <span className="pointer-events-none absolute right-4 top-4 h-9 w-9 border-r-2 border-t-2 border-[#D4AF37] sm:right-6 sm:top-6" />
                <span className="pointer-events-none absolute bottom-4 left-4 h-9 w-9 border-b-2 border-l-2 border-[#D4AF37] sm:bottom-6 sm:left-6" />
                <span className="pointer-events-none absolute bottom-4 right-4 h-9 w-9 border-b-2 border-r-2 border-[#D4AF37] sm:bottom-6 sm:right-6" />
            </motion.div>

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 flex min-h-screen flex-col justify-end px-5 pb-24 sm:px-10 sm:pb-16 lg:px-16"
            >
                <h1
                    data-testid="hero-title"
                    className="font-display text-[clamp(3rem,9vw,8.5rem)] font-medium leading-[0.92] tracking-tight text-white [text-shadow:0_4px_40px_rgba(5,5,5,0.9)]"
                >
                    <HeroLine delay={0.25}>LE GARS</HeroLine>
                    <HeroLine delay={0.4}>
                        <span className="font-light">DE LA </span>
                        <span className="text-gold-grad italic">PHOTO</span>
                    </HeroLine>
                </h1>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.85, duration: 0.9 }}
                    className="mt-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between"
                >
                    <div>
                        <p
                            data-testid="hero-subtitle"
                            className="font-mono-lux text-xs tracking-[0.3em] text-[#E2E2E2] sm:text-sm"
                        >
                            DANIEL DULUDE{" "}
                            <span className="mx-2 text-[#D4AF37]">—</span>{" "}
                            PHOTOGRAPHE
                        </p>
                        <p
                            data-testid="hero-tagline"
                            className="font-display mt-4 text-xl font-light italic text-[#E2E2E2]/90 sm:text-2xl"
                        >
                            « Des images qui racontent votre histoire. »
                        </p>
                    </div>
                </motion.div>
            </motion.div>

            <motion.button
                data-testid="hero-scroll-button"
                onClick={onScrollDown}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="absolute bottom-8 right-6 z-10 hidden flex-col items-center gap-3 sm:right-10 sm:flex lg:right-16"
                aria-label="Défiler vers les spécialités"
            >
                <span className="font-mono-lux text-[9px] tracking-[0.4em] text-[#8A8A8E]">
                    DÉFILER
                </span>
                <span className="scroll-line-anim block h-12 w-px bg-gradient-to-b from-[#D4AF37] to-transparent" />
            </motion.button>
        </section>
    );
}
